import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import {
  getCachedPdfPreview,
  renderPdfPreview,
  type PdfPreview,
} from '@/lib/pdfjs';

interface Props {
  url: string;
}

/**
 * Couverture d'un catalogue : la première page du PDF, rasterisée.
 *
 * Le rendu ne démarre que lorsque la carte approche de l'écran — une grille de
 * vingt catalogues ne doit pas déclencher vingt téléchargements d'un coup sur
 * une 4G tunisienne.
 */
export function PdfThumbnail({ url }: Props) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<PdfPreview | null>(() =>
    getCachedPdfPreview(url),
  );
  const [failed, setFailed] = useState(false);
  const holderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cached = getCachedPdfPreview(url);
    if (cached) {
      setPreview(cached);
      return;
    }
    setPreview(null);
    setFailed(false);

    const el = holderRef.current;
    if (!el) return;

    let cancelled = false;
    const start = () => {
      renderPdfPreview(url)
        .then((result) => {
          if (!cancelled) setPreview(result);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          start();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [url]);

  return (
    <div ref={holderRef} className="absolute inset-0">
      {preview ? (
        <img
          src={preview.dataUrl}
          alt=""
          className="w-full h-full object-contain"
        />
      ) : failed ? (
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ color: 'var(--pro-text-grey-light)' }}
        >
          <FileText className="w-8 h-8" />
        </div>
      ) : (
        <div className="w-full h-full animate-pulse" />
      )}

      {preview && preview.pageCount > 1 && (
        <span
          className="absolute bottom-2 end-2 px-2 h-6 inline-flex items-center rounded-lg text-[11px] font-semibold text-white"
          style={{ background: 'rgba(0, 0, 0, 0.62)', fontFamily: 'Figtree' }}
        >
          {/* Le badge ne s'affiche qu'au-delà d'une page : pas de pluriel à
              gérer, donc pas de `count` i18next. */}
          {t('pro.catalogue.pages', '{{total}} pages', {
            total: preview.pageCount,
          })}
        </span>
      )}
    </div>
  );
}
